(ns ui.openrouter.modal
  "Generic generation modal for all OpenRouter models. The form fields
   adapt to the selected model's category and capabilities (aspect
   ratios, voices, durations, resolutions, source image for editing)."
  (:require [app.openrouter.image-pricing :as image-pricing]
            [app.openrouter.pricing :as pricing]
            [clojure.string :as str]
            [datascript.core :as ds]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [storrito.ui.button :as button]
            [ui.ai.core :as core]
            [ui.editor2.extension-points :as extension-points]
            [ui.openrouter.catalog :as catalog]))

(defn modal-params
  [state]
  (get-in state [:ui/modal :params]))

(defn form-data
  [state]
  (get-in state [:ui/modal :openrouter/data]))

(defn prompt-field-key
  [category-id]
  (if (= :tts category-id)
    :text
    :prompt))

(defn draft-path
  "Drafts survive closing the modal, but are session local: an
   unsubmitted prompt is not part of the design."
  [category-id]
  [:editor2 :ai-sidebar :draft category-id])

(defn selected-clip-ai-prompt
  "The prompt of the clip that is selected in the timeline, when its
   asset was AI generated in the same category. Selecting an AI music
   track and opening the music modal then starts from that track's
   prompt."
  [state category-id]
  (let [db (get-in state [:editor2 :db])
        clip-uuid (get-in state [:editor2 :selected-clip])]
    (when (and db clip-uuid)
      (let [data (some-> (ds/entity db [:clip/uuid clip-uuid])
                         (:clip/asset)
                         (:asset/data))]
        (when (= category-id (:ai/category data))
          (get data
               (if (= :tts category-id)
                 :ai/text
                 :ai/prompt)))))))

(defn last-ai-prompt
  "The prompt of the most recently generated AI asset of the same
   category, taken from the :asset/data provenance in the design's
   DataScript db."
  [db category-id]
  (when db
    (let [field (if (= :tts category-id)
                  :ai/text
                  :ai/prompt)]
      (->> (ds/q '[:find [?e ...]
                   :where [?e :asset/data _]]
                 db)
           (sort >)
           (keep (fn [eid]
                   (let [data (:asset/data (ds/entity db eid))]
                     (when (= category-id (:ai/category data))
                       (get data field)))))
           (first)))))

(defn ai-asset-data
  "The generation recipe that is stored as :asset/data provenance on the
   generated asset. Provider neutral :ai/* keys, so the design file
   format does not depend on OpenRouter."
  [state]
  (let [{:keys [model category-id]} (modal-params state)
        {:keys [prompt text voice aspect-ratio resolution duration
                source-asset-uuid]} (form-data state)]
    (cond-> {:ai/model-id (:model/id model)
             :ai/category category-id}
      (seq prompt) (assoc :ai/prompt prompt)
      (seq text) (assoc :ai/text text)
      voice (assoc :ai/voice voice)
      aspect-ratio (assoc :ai/aspect-ratio aspect-ratio)
      resolution (assoc :ai/resolution resolution)
      duration (assoc :ai/duration duration)
      source-asset-uuid (assoc :ai/source-asset-uuid source-asset-uuid))))

(defn current-model
  "The live catalog entry of the modal's model. The regenerate workflow can
   open the modal before the catalog was ever loaded, with only a stub
   model map — the catalog entry replaces it once it is available."
  [state]
  (let [{:keys [model category-id]} (modal-params state)]
    (or (some->> (:openrouter/categories
                   (query/get-result state catalog/models-query))
                 (filter #(= category-id (:category/id %)))
                 (first)
                 (:category/models)
                 (filter #(= (:model/id model) (:model/id %)))
                 (first))
        model)))

(defn generate-command
  [state]
  (let [{:keys [model category-id]} (modal-params state)
        {:keys [prompt text voice aspect-ratio resolution
                source-image-url]} (form-data state)]
    (case category-id
      :tts {:command/kind :command/openrouter-generate-speech
            :command/data {:model-id (:model/id model)
                           :text text
                           :voice voice}}
      :music {:command/kind :command/openrouter-generate-music
              :command/data {:model-id (:model/id model)
                             :prompt prompt}}
      ;; Sends the options of the displayed estimate, including the
      ;; defaults of a form that was opened before the catalog was loaded.
      ;; `video-options` also parses the <select> string of the duration:
      :video (let [options (pricing/video-options (current-model state)
                                                  (form-data state))]
               {:command/kind :command/openrouter-video-start
                :command/data (cond-> {:model-id (:model/id model)
                                       :prompt prompt}
                                (:aspect-ratio options) (assoc :aspect-ratio (:aspect-ratio options))
                                (:resolution options) (assoc :resolution (:resolution options))
                                (:duration options) (assoc :duration (:duration options)))})
      {:command/kind :command/openrouter-generate-image
       :command/data (cond-> {:model-id (:model/id model)
                              :category category-id
                              :prompt prompt}
                       aspect-ratio (assoc :aspect-ratio aspect-ratio)
                       resolution (assoc :resolution resolution)
                       source-image-url (assoc :source-image-url
                                               source-image-url))})))

(defn submit-actions
  "The error and spinner state of the modal is keyed by the command that
   was actually submitted (stored under :openrouter/submitted-command),
   not by a command map rebuilt from the live form data — otherwise
   typing a prompt that matches an earlier failed attempt would surface
   that stale error while typing."
  [state]
  (let [{:keys [category-id regenerate-asset-uuid]} (modal-params state)
        command (generate-command state)
        ai-data (ai-asset-data state)
        ;; A successful generation stores the prompt as provenance on
        ;; the asset, so the session draft is cleared:
        clear-draft [:store/assoc-in (draft-path category-id) nil]
        ;; When regenerating an existing asset, the new asset replaces
        ;; it in the timeline (the old asset stays available in the
        ;; media sidebar):
        insert-params (cond-> {:command command
                               :ai-data ai-data}
                        regenerate-asset-uuid
                        (assoc :replace-asset-uuid regenerate-asset-uuid))
        command-action (case category-id
                         (:tts :music)
                         [:data/command command
                          {:on-success [[:editor2/add-ai-audio insert-params]
                                        clear-draft
                                        [:ui/modal-close]]
                           :on-error [[:ui/show-error]]}]
                         :video [:data/command command
                                 {:on-success [[:openrouter/video-poll-start insert-params]
                                               clear-draft]
                                  :on-error [[:ui/show-error]]}]
                         [:data/command command
                          {:on-success [[:editor2/add-openrouter-image
                                         (assoc insert-params
                                                :default-height 1920
                                                :default-width 1080)]
                                        clear-draft
                                        [:store/assoc-in [:editor2 :sidebar-panel] :media]
                                        [:ui/modal-close]]
                           :on-error [[:ui/show-error]]}])]
    [[:store/assoc-in [:ui/modal :openrouter/submitted-command] command]
     command-action]))

(defn select-field
  [{:keys [label field options selected]}]
  [:div.flex.flex-col.mb-3
   [:label.text-sm.font-medium.text-gray-700.mb-1
    label]
   [:select.border.border-gray-300.rounded.p-2.focus:outline-none.focus:ring.focus:ring-blue-500
    {:on {:change [[:store/assoc-in [:ui/modal :openrouter/data field]
                    :event/target.value]]}}
    (map (fn [option]
           [:option {:value (str option)
                     :selected (= (str option) (str selected))}
            (str option)])
         options)]])

(defn prompt-field
  [{:keys [field value placeholder draft-category]}]
  [:textarea.border.border-gray-300.rounded.p-3.focus:outline-none.focus:ring.focus:ring-blue-500.focus:border-blue-500.h-40.w-full
   {:placeholder placeholder
    :value value
    ;; Typing marks the prompt as user written (prefills never overwrite
    ;; it) and mirrors it into the session draft, which survives closing
    ;; the modal:
    :on {:input [[:store/assoc-in [:ui/modal :openrouter/data field]
                  :event/target.value]
                 [:store/assoc-in [:ui/modal :openrouter/prompt-source]
                  :user]
                 [:store/assoc-in (draft-path draft-category)
                  :event/target.value]]}}])

(defn q-image-assets
  [db]
  (when db
    (->> (ds/q '[:find [?asset-uuid ...]
                 :where
                 [?asset :asset/uuid ?asset-uuid]
                 [?asset :asset/type ?type]
                 [(contains? #{:image :gif} ?type)]]
               db)
         (map (fn [asset-uuid]
                (ds/entity db [:asset/uuid asset-uuid]))))))

(defn asset-source-url
  "The server reachable URL of an asset: the uploaded URI, or the
   original URI when it is already a http(s)/blob-store URL."
  [asset]
  (let [uri (:asset/uri asset)]
    (or (:asset/uploaded-uri asset)
        (when (and (string? uri)
                   (or (.startsWith uri "/blob/")
                       (.startsWith uri "http")))
          uri))))

(defn source-image-picker
  [state]
  (let [assets (q-image-assets (get-in state [:editor2 :db]))
        selected-uuid (get-in state [:ui/modal :openrouter/data :source-asset-uuid])]
    [:div.mb-3
     [:label.text-sm.font-medium.text-gray-700.mb-1.block
      "Source image"]
     (if (empty? assets)
       [:p.text-sm.text-gray-500
        "Add an image to your story first, then select it here for editing."]
       [:div.flex.flex-wrap.gap-2.max-h-64.overflow-y-auto
        (for [asset assets]
          (let [uploading? (nil? (asset-source-url asset))]
            ;; Story assets are usually 9:16, so the tiles are portrait:
            [:div.relative.rounded-lg.overflow-hidden.cursor-pointer.bg-gray-100.flex.items-center.justify-center
             {:class (when (= selected-uuid (:asset/uuid asset))
                       ["ring-2" "ring-blue-500"])
              :style {:width "4.5rem"
                      :height "8rem"}
              :on {:click (when-not uploading?
                            [[:openrouter/select-source-image
                              {:asset-uuid (:asset/uuid asset)
                               :source-image-url (asset-source-url asset)
                               ;; AI generated source images carry their
                               ;; prompt, which prefills the textarea
                               ;; (unless the user typed one):
                               :prompt (get-in asset
                                               [:asset/data :ai/prompt])}]])}}
             [:img.max-h-full.max-w-full.object-contain.pointer-events-none
              {:src (extension-points/asset-thumbnail {:asset asset
                                                       :state state})}]
             (when uploading?
               [:div.absolute.inset-0.bg-white.bg-opacity-70.flex.items-center.justify-center
                [:span.text-xs.text-gray-600 "uploading…"]])]))])]))

(defn video-progress-panel
  [state]
  (let [error (get-in state [:ui/modal :openrouter/video-error])]
    [:div.flex.items-center.w-full.justify-center.flex-col.py-8
     (if error
       [:div.flex.flex-col.items-center
        [:p {:class "text-red-500 text-s italic mb-4"}
         "ERROR : " error]
        [:button
         {:on {:click [[:crisp/open-chat]]}
          :type "button"
          :class button/button-secondary-css}
         "Contact Support"]]
       [:div.flex.flex-col.items-center
        [:div.w-16.h-16.border-4.border-blue-500.border-t-transparent.rounded-full.animate-spin]
        [:p.mt-5.text-gray-700
         "Generating your video…"]
        [:p.mt-1.text-sm.text-gray-500
         "This can take a few minutes. The video is added to your story
          automatically when it is ready. You can close this dialog."]])]))

(defn form-fields
  [state]
  (let [{:keys [category-id]} (modal-params state)
        model (current-model state)
        data (form-data state)]
    (case category-id
      :tts
      [:div.w-full
       [:label.text-sm.font-medium.text-gray-700.mb-1.block
        "Text"]
       (prompt-field {:field :text
                      :value (:text data)
                      :draft-category :tts
                      :placeholder "The text that should be spoken…"})
       (when (seq (:model/voices model))
         (select-field {:label "Voice"
                        :field :voice
                        :options (:model/voices model)
                        :selected (:voice data)}))]

      :music
      [:div.w-full
       [:label.text-sm.font-medium.text-gray-700.mb-1.block
        "Prompt"]
       (prompt-field {:field :prompt
                      :value (:prompt data)
                      :draft-category :music
                      :placeholder "Describe the music: genre, mood, instruments…"})]

      ;; The selects show the options of the displayed estimate, including
      ;; the defaults of a form that was opened before the catalog loaded:
      :video
      (let [selected (pricing/video-options model data)]
        [:div.w-full
         [:label.text-sm.font-medium.text-gray-700.mb-1.block
          "Prompt"]
         (prompt-field {:field :prompt
                        :value (:prompt data)
                        :draft-category :video
                        :placeholder "Describe your video scene…"})
         (when (seq (:model/aspect-ratios model))
           (select-field {:label "Aspect ratio"
                          :field :aspect-ratio
                          :options (:model/aspect-ratios model)
                          :selected (:aspect-ratio selected)}))
         (when (seq (:model/resolutions model))
           (select-field {:label "Resolution"
                          :field :resolution
                          :options (:model/resolutions model)
                          :selected (:resolution selected)}))
         (when (seq (:model/durations model))
           (select-field {:label "Duration (seconds)"
                          :field :duration
                          :options (:model/durations model)
                          :selected (:duration selected)}))])

      [:div.w-full
       (when (= :image-editing category-id)
         (source-image-picker state))
       [:label.text-sm.font-medium.text-gray-700.mb-1.block
        "Prompt"]
       (prompt-field {:field :prompt
                      :value (:prompt data)
                      :draft-category category-id
                      :placeholder (if (= :image-editing category-id)
                                     "Describe how the image should be changed…"
                                     "Describe your idea or scene…")})
       (when (seq (:model/aspect-ratios model))
         (select-field {:label "Aspect ratio"
                        :field :aspect-ratio
                        :options (:model/aspect-ratios model)
                        :selected (:aspect-ratio data)}))
       (when (seq (:model/resolutions model))
         (select-field {:label "Resolution"
                        :field :resolution
                        :options (:model/resolutions model)
                        :selected (:resolution data)}))])))

(defn submittable?
  [state]
  (let [{:keys [category-id]} (modal-params state)
        data (form-data state)]
    (case category-id
      :tts (seq (:text data))
      :image-editing (and (seq (:prompt data))
                          (:source-image-url data))
      (seq (:prompt data)))))

(defn submit-label
  [category-id]
  (case category-id
    :tts "Generate Speech"
    :music "Generate Music"
    :video "Generate Video"
    "Generate Image"))

(defn model-select
  "Switches to another model of the same category, keeping the entered
   form data."
  [state category-id current-model]
  (let [models (some->> (:openrouter/categories
                          (query/get-result state catalog/models-query))
                        (filter #(= category-id (:category/id %)))
                        (first)
                        (:category/models))]
    (when (seq models)
      [:div.flex.flex-col.mb-3
       [:label.text-sm.font-medium.text-gray-700.mb-1
        "Model"]
       [:select.border.border-gray-300.rounded.p-2.focus:outline-none.focus:ring.focus:ring-blue-500
        {:on {:change [[:openrouter/switch-model :event/target.value]]}}
        (map (fn [model]
               [:option {:value (:model/id model)
                         :selected (= (:model/id model)
                                      (:model/id current-model))}
                (str (:model/name model)
                     (when-let [price-label (cond
                                              (not= (:model/id model) (:model/id current-model))
                                              (:model/price-label model)

                                              (#{:image-generation :image-editing} category-id)
                                              (image-pricing/price-label model category-id (form-data state))

                                              :else
                                              (pricing/modal-price-label model category-id (form-data state)))]
                       (str " — " price-label)))])
             models)]])))

(defn panel
  [state]
  (let [{:keys [category-id]} (modal-params state)
        query-result (query/get-result state catalog/models-query)
        model (current-model state)
        image? (#{:image-generation :image-editing} category-id)
        image-estimate (when image?
                         (image-pricing/estimate model category-id (form-data state)))
        submitted-command (get-in state [:ui/modal :openrouter/submitted-command])]
    (cond
      (not (:organization/positive-credit-balance? query-result))
      (core/upgrade-panel query-result)

      (and (= :video category-id)
           (get-in state [:ui/modal :openrouter/video-job-uuid]))
      (video-progress-panel state)

      (and submitted-command
           (command/error? state submitted-command))
      (let [error (command/get-latest-error state submitted-command)]
        [:div.flex.items-center.w-full.justify-center.flex-col
         [:label.text-gray-800.block.text-base.font-bold.mb-2
          "Getting the following error : "]
         [:p {:class "text-red-500 text-s italic min-h-8"}
          "ERROR : " error]
         [:button.mt-5
          {:on {:click [[:crisp/open-chat]]}
           :type "button"
           :class button/button-secondary-css}
          "Contact Support"]])

      :else
      [:div.flex.items-center.w-full.justify-center.flex-col
       {:replicant/key :openrouter-panel}
       (when (and submitted-command
                  (command/issued? state submitted-command))
         [:div.absolute.flex.items-center.w-full.justify-center.flex-col.h-full.bg-white.opacity-80.z-10
          [:div.w-16.h-16.border-4.border-blue-500.border-t-transparent.rounded-full.animate-spin]
          [:span.mt-5
           "Loading ..."]])

       [:div.w-full.max-w-2xl.mx-auto
        [:div.flex.mb-1.items-center.w-full
         [:h2.text-xl.font-semibold.text-gray-800
          (:model/name model)]]
        [:p.text-sm.text-gray-500.mb-4.w-full
         (:model/description model)]

        (model-select state category-id model)

        (form-fields state)]

       [:div.flex.items-center.w-full.justify-center.flex-col
        [:button.mt-4
         {:disabled (or (not (submittable? state))
                        (and (= :video category-id)
                             (nil? (pricing/video-price model (form-data state)))))
          :on {:click (submit-actions state)}
          :type "button"
          :class button/button-primary-css}
         (submit-label category-id)]
        [:div.mt-2.text-sm.text-gray-500
         (when-not (and image? (nil? image-estimate))
           "Estimated cost ")
         (if image?
           (image-pricing/price-label model category-id (form-data state))
           (pricing/modal-price-label model category-id (form-data state)))
         [:div (if (and image? (nil? image-estimate))
                 "No fixed price for these settings. You pay the actual usage, including OpenRouter fees and Storrito markup."
                 "Includes OpenRouter fees and Storrito markup. Final charge uses actual provider usage.")]]]])))

(defn render
  [state]
  [:div
   (panel state)])

(defn regenerate-ai-data
  "The :ai/* provenance of the asset that is being regenerated, when the
   modal was opened via the regenerate workflow (timeline double click
   or the AI badge in the media sidebar)."
  [state]
  (when-let [asset-uuid (:regenerate-asset-uuid (modal-params state))]
    (some-> (ds/entity (get-in state [:editor2 :db])
                       [:asset/uuid asset-uuid])
            (:asset/data))))

(defn source-image-url-of-asset
  [state asset-uuid]
  (when asset-uuid
    (some-> (ds/entity (get-in state [:editor2 :db])
                       [:asset/uuid asset-uuid])
            (asset-source-url))))

(def modal
  {:modal-id :modals/openrouter-generate
   :on-load (fn [state]
              (let [{:keys [model category-id]} (get-in state [:ui/modal :params])
                    regen (regenerate-ai-data state)
                    field (prompt-field-key category-id)
                    draft (get-in state (draft-path category-id))
                    ;; Prefill precedence: the asset being regenerated
                    ;; (explicit user intent), then an unsubmitted user
                    ;; draft, then the prompt of the selected clip (when
                    ;; it is an AI asset of this category), then the
                    ;; prompt of the newest generation of this category:
                    regen-prompt (get regen
                                      (if (= :tts category-id)
                                        :ai/text
                                        :ai/prompt))
                    prefill (when-not regen-prompt
                              (if-not (str/blank? draft)
                                draft
                                (or (selected-clip-ai-prompt state category-id)
                                    (last-ai-prompt (get-in state [:editor2 :db])
                                                    category-id))))
                    prompt-value (or regen-prompt prefill)
                    prompt-source (if (and (not regen-prompt)
                                           (not (str/blank? draft)))
                                    :user
                                    :prefill)
                    aspect-ratio (or (:ai/aspect-ratio regen)
                                     (pricing/default-aspect-ratio (:model/aspect-ratios model)))
                    voice (when (= :tts category-id)
                            (or (:ai/voice regen)
                                (first (:model/voices model))))
                    resolution (when (#{:video :image-generation :image-editing} category-id)
                                 (or (:ai/resolution regen)
                                     (pricing/default-resolution (:model/resolutions model))))
                    duration (when (= :video category-id)
                               (or (:ai/duration regen)
                                   (pricing/default-duration (:model/durations model))))
                    source-image-url (when (= :image-editing category-id)
                                       (source-image-url-of-asset
                                         state
                                         (:ai/source-asset-uuid regen)))]
                (cond-> [[:data/query catalog/models-query]]
                  (seq prompt-value)
                  (into [[:store/assoc-in [:ui/modal :openrouter/data field] prompt-value]
                         [:store/assoc-in [:ui/modal :openrouter/prompt-source] prompt-source]])

                  aspect-ratio
                  (conj [:store/assoc-in
                         [:ui/modal :openrouter/data :aspect-ratio]
                         aspect-ratio])

                  voice
                  (conj [:store/assoc-in
                         [:ui/modal :openrouter/data :voice]
                         voice])

                  resolution
                  (conj [:store/assoc-in
                         [:ui/modal :openrouter/data :resolution]
                         resolution])

                  duration
                  (conj [:store/assoc-in
                         [:ui/modal :openrouter/data :duration]
                         duration])

                  source-image-url
                  (into [[:store/assoc-in
                          [:ui/modal :openrouter/data :source-asset-uuid]
                          (:ai/source-asset-uuid regen)]
                         [:store/assoc-in
                          [:ui/modal :openrouter/data :source-image-url]
                          source-image-url]]))))
   :render #'render})
